import { yupResolver } from "@hookform/resolvers/yup";
import { CircularProgress } from "@mui/material";
import moment from "moment";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import * as yup from "yup";

import { api } from "../../../../lib/api";
import { usePartnerData } from "../../../../providers/partnerData";
import { useUser } from "../../../../providers/user";

import { IDates } from "../../../../types/SearchAvalilableTimes";
import { IHealthyInsurance } from "../../../../types/doctor";
import { ICreditPaymentoForm } from "../../../../types/forms";

import { validateBirthDate, validateCpf } from "../../../../utils/formValidations";

import BillingAddressSection from "../../BillingAddressSection";
import CardHolderSection from "../../CardHolderSection";
import CreditCardSection from "../../CreditCardSection";

interface ITicketForm {
  schedule: IDates;
  healthyInsurance: IHealthyInsurance;
  reportMessage: string;
}

type ErrorMessages = {
  [key: string]: string[];
};

const TicketForm = ({
  schedule,
  healthyInsurance,
  reportMessage,
}: ITicketForm) => {
  const navigate = useNavigate();
  const { userData } = useUser();
  const { partnerUrl } = usePartnerData();

  const [processingPayment, setProcessingPayment] = useState<boolean>(false);

  const CardSchema = yup.object().shape({
    cardNumber: yup.string().required("Campo obrigatorio"),
    securityCode: yup.string().required("Campo obrigatorio"),
    nameInCard: yup.string().required("Campo obrigatorio"),
    monthExpiration: yup.string().required("Campo obrigatorio").max(2),
    yearExpiration: yup.string().required("Campo obrigatorio").max(2),

    cpfOwner: yup
      .string()
      .required("Campo obrigatorio")
      .test("cpfValidator", "*CPF inválido", validateCpf),
    phoneOwner: yup.string().required("Campo obrigatorio"),
    birthdayOwner: yup
      .string()
      .required("Campo obrigatorio")
      .transform((value) =>
        value ? moment(value, "DD/MM/YYYY").format("YYYY-MM-DD") : ""
      )
      .test("birthDateValidator", "*Data inválida", validateBirthDate),

    addressCep: yup.string().required("Campo obrigatorio"),
    addressRoad: yup.string().required("Campo obrigatorio"),
    addressNumber: yup.string().required("Campo obrigatorio"),
    addressComplement: yup.string().required("Campo obrigatorio"),
    addressDistrict: yup.string().required("Campo obrigatorio"),
    addressCity: yup.string().required("Campo obrigatorio"),
    addressState: yup
      .string()
      .required("Campo obrigatorio")
      .test("selectState", "*Selecione um Estado", (inputValue) =>
        inputValue === "selecione" ? false : true
      ),
  });

  const methods = useForm<ICreditPaymentoForm>({
    resolver: yupResolver(CardSchema),
  });

  const { handleSubmit } = methods;

  function sendErrorMessage(text?: ErrorMessages) {
    const message = text
      ? `<ul>${Object.entries(text)
        .flatMap(([_, msgs]) =>
          msgs.map((msg) => `<li class="text-start">${msg}</li>`)
        )
        .join("")}</ul>`
      : "Por favor, confira a data e os dados inseridos.";

    return Swal.fire({
      title: "Algo deu errado",
      html: message,
      icon: "warning",
      showCancelButton: false,
      buttonsStyling: false,
      allowOutsideClick: false,
      confirmButtonText: "Fechar",
      customClass: {
        confirmButton: "btn btn-lg fw-semibold btn-warning mx-1",
      },
    });
  }

  const onSubmit = async (data: ICreditPaymentoForm) => {
    try {
      setProcessingPayment(true);
      const expirationDate = `${data.monthExpiration}/20${data.yearExpiration}`;

      const {
        cardNumber,
        nameInCard,
        securityCode,
        addressNumber,
        addressRoad,
        addressComplement,
        addressDistrict,
        addressCity,
        addressState,
        addressCep,
      } = data;

      const body = {
        scheduleTimeId: schedule.agendas[0].idSchedule,
        healthInsuranceId:
          healthyInsurance.name.toLowerCase() === "particular"
            ? undefined
            : healthyInsurance.id,
        date: schedule.data,
        scheduleType: schedule.agendas[0].tipo,
        creditCardObject: {
          cardNumber: cardNumber,
          holder: nameInCard,
          expirationDate,
          securityCode: securityCode,
        },
        addressNumber,
        addressRoad,
        addressComplement,
        addressDistrict,
        addressCity,
        addressState,
        addressCountry: "brasil",
        addressCep,
        reportMessage,
      };

      const req = await api.post("/Attendance/tele", body);
      const reqTele = req.data;

      if (!reqTele) {
        setProcessingPayment(false);
        navigate(`/${partnerUrl}/consultas`);
      } else {
        sendErrorMessage();
        setProcessingPayment(false);
      }
    } catch (error: any) {
      console.error(error);
      if (error?.response?.data) {
        sendErrorMessage(error?.response?.data);
      } else {
        sendErrorMessage();

      }
      setProcessingPayment(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="d-flex flex-column gap-3">
          <CardHolderSection userData={userData} />
          <hr />
          <BillingAddressSection userData={userData} />
          <hr />
          <div className="text-center d-flex gap-3 align-items-center justify-content-between">
            <button onClick={() => window.history.back()} className="btn btn-outline-secondary">
              <i className="las la-arrow-left me-2"></i>Voltar
            </button>
            <button
              disabled={processingPayment}
              className="btn btn-success fw-semibold"
              type="submit"
            >
              {processingPayment ? (
                <CircularProgress size={21} color="inherit" />
              ) : (
                <>
                  <i className="las la-check me-2"></i>
                  Confirmar Teleconsulta
                </>
              )}
            </button>
          </div>
        </div>

      </form>
    </FormProvider>
  );
};

export default TicketForm;


